import { useMemo } from 'react';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { useTenantFeatureGates } from './useTenantFeatureGates';
import { TenantFeatureGatesEnum } from '../../constants/tenantFeatureGates';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';

export interface UseVirtualAgentFeatureGateProps {
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface VirtualAgentFeatureGate {
  configuration: boolean;
  jobBooking: boolean;
  followUps: boolean;
  loading: boolean;
}

/**
 * Hook to check if Virtual Agent features are enabled for the organization.
 * Checks all tenants in the user's organization - if ANY tenant has the feature gate enabled,
 * OR if the corresponding feature flag is enabled, the feature is available network-wide.
 *
 * @param skip - Skip the feature gate query (default: false)
 * @param fetchPolicy - Apollo fetch policy for the query (default: 'network-only')
 * @returns Object with configuration, jobBooking, followUps booleans and loading state
 */
export const useVirtualAgentFeatureGate = ({
  skip = false,
  fetchPolicy = 'network-only',
}: UseVirtualAgentFeatureGateProps = {}): VirtualAgentFeatureGate => {
  const { user } = useAuth();
  const tenantIds = useMemo(
    () => user?.org?.tenants?.map((t) => t.id) ?? [],
    [user],
  );

  const {
    [FeatureFlagKeys.featureVirtualAgentConfiguration]:
      featureVirtualAgentConfiguration,
    [FeatureFlagKeys.featureVirtualAssistantJobBooking]:
      featureVirtualAssistantJobBooking,
    [FeatureFlagKeys.featureVirtualAssistantFollowUps]:
      featureVirtualAssistantFollowUps,
  } = useFeatureFlags();

  const { data, loading } = useTenantFeatureGates({
    tenantIds,
    featureGateKeys: [
      TenantFeatureGatesEnum.EnableVoiceAgentForContactCenterPro,
    ],
    skip,
    fetchPolicy,
  });

  const isFeatureGateEnabled = useMemo(() => {
    const featureGate = data?.tenantFeatureGates.find(
      (gate) =>
        gate.key === TenantFeatureGatesEnum.EnableVoiceAgentForContactCenterPro,
    );
    const value = featureGate?.value;
    return value && 'boolValue' in value ? value.boolValue : false;
  }, [data]);

  return {
    configuration:
      isFeatureGateEnabled ||
      Boolean(featureVirtualAgentConfiguration ?? false),
    jobBooking:
      isFeatureGateEnabled ||
      Boolean(featureVirtualAssistantJobBooking ?? false),
    followUps:
      isFeatureGateEnabled ||
      Boolean(featureVirtualAssistantFollowUps ?? false),
    loading,
  };
};
